import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, Calendar, Clock, ChevronLeft, ChevronRight, 
  TrendingUp, TrendingDown, Minus, Filter, Globe, 
  AlertTriangle, Bell, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EconomicCalendar = () => {
  const [events, setEvents] = useState([]);
  const [eventsByDate, setEventsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekInfo, setWeekInfo] = useState({ start: '', end: '' });
  
  // Filters
  const [impactFilter, setImpactFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [view, setView] = useState('week');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [weekOffset, impactFilter, countryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/calendar/week?week_offset=${weekOffset}`;
      if (impactFilter !== 'all') {
        url += `&impact=${impactFilter}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      // Apply country filter on frontend
      let filteredEvents = data.events_by_date;
      if (countryFilter !== 'all') {
        filteredEvents = {};
        Object.keys(data.events_by_date).forEach(date => {
          const filtered = data.events_by_date[date].filter(e => e.country === countryFilter);
          if (filtered.length > 0) {
            filteredEvents[date] = filtered;
          }
        });
      }
      
      setEventsByDate(filteredEvents);
      setWeekInfo({ start: data.week_start, end: data.week_end });
      
      // Flatten events for stats
      const allEvents = Object.values(filteredEvents).flat();
      setEvents(allEvents);
    } catch (error) {
      toast.error('Failed to fetch economic calendar');
    } finally {
      setLoading(false);
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getImpactBadge = (impact) => {
    switch (impact) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'low': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSurpriseIndicator = (event) => {
    if (!event.actual || event.surprise === null) return null;
    
    const surprise = event.surprise;
    if (Math.abs(surprise) < 0.1) {
      return <Minus className="w-4 h-4 text-muted-foreground" />;
    }
    if (surprise > 0) {
      return <TrendingUp className="w-4 h-4 text-primary" />;
    }
    return <TrendingDown className="w-4 h-4 text-destructive" />;
  };

  const getCountryFlag = (country) => {
    const flags = {
      'USD': '🇺🇸',
      'EUR': '🇪🇺',
      'GBP': '🇬🇧',
      'JPY': '🇯🇵',
      'AUD': '🇦🇺',
      'CAD': '🇨🇦',
      'CHF': '🇨🇭',
      'NZD': '🇳🇿'
    };
    return flags[country] || '🏳️';
  };

  const formatWeekRange = () => {
    if (!weekInfo.start || !weekInfo.end) return '';
    const start = new Date(weekInfo.start);
    const end = new Date(weekInfo.end);
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}, ${end.getFullYear()}`;
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Stats
  const highImpactCount = events.filter(e => e.impact === 'high').length;
  const mediumImpactCount = events.filter(e => e.impact === 'medium').length;
  const lowImpactCount = events.filter(e => e.impact === 'low').length;

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="economic-calendar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Calendar className="w-8 h-8 text-primary" />
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight" data-testid="calendar-title">
              Economic <span className="text-primary">Calendar</span>
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            Stay ahead of market-moving events. Track high-impact news releases that affect your trading.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3 md:gap-4 mb-6"
        >
          <Card className="bg-card border-red-500/30">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-xs md:text-sm text-muted-foreground">High Impact</span>
              </div>
              <p className="font-mono text-2xl md:text-3xl font-bold text-red-400 mt-1">{highImpactCount}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-orange-500/30">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span className="text-xs md:text-sm text-muted-foreground">Medium</span>
              </div>
              <p className="font-mono text-2xl md:text-3xl font-bold text-orange-400 mt-1">{mediumImpactCount}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-yellow-500/30">
            <CardContent className="p-3 md:p-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-xs md:text-sm text-muted-foreground">Low</span>
              </div>
              <p className="font-mono text-2xl md:text-3xl font-bold text-yellow-400 mt-1">{lowImpactCount}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filters & Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                {/* Week Navigation */}
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setWeekOffset(prev => prev - 1)}
                    data-testid="prev-week"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="px-4 py-2 bg-secondary/30 rounded min-w-[200px] text-center">
                    <span className="font-heading font-bold">{formatWeekRange()}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setWeekOffset(prev => prev + 1)}
                    data-testid="next-week"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  {weekOffset !== 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setWeekOffset(0)}
                      className="text-primary"
                    >
                      Today
                    </Button>
                  )}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Select value={impactFilter} onValueChange={setImpactFilter}>
                      <SelectTrigger className="w-[130px] bg-input/50" data-testid="impact-filter">
                        <SelectValue placeholder="Impact" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Impact</SelectItem>
                        <SelectItem value="high">High Only</SelectItem>
                        <SelectItem value="medium">Medium Only</SelectItem>
                        <SelectItem value="low">Low Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Select value={countryFilter} onValueChange={setCountryFilter}>
                    <SelectTrigger className="w-[130px] bg-input/50" data-testid="country-filter">
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {categories?.countries?.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={fetchEvents}
                    disabled={loading}
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Calendar Events */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {Object.keys(eventsByDate).length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No events found for the selected filters</p>
                </CardContent>
              </Card>
            ) : (
              Object.keys(eventsByDate).sort().map((date) => (
                <Card 
                  key={date} 
                  className={`bg-card border-border ${isToday(date) ? 'border-primary/50 ring-1 ring-primary/20' : ''}`}
                  data-testid={`calendar-day-${date}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                          {getDayName(date)}
                          {isToday(date) && (
                            <Badge className="bg-primary text-primary-foreground text-xs">TODAY</Badge>
                          )}
                        </CardTitle>
                        <span className="text-sm text-muted-foreground">{formatDateDisplay(date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {eventsByDate[date].length} events
                        </Badge>
                        {eventsByDate[date].some(e => e.impact === 'high') && (
                          <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            High Impact
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-xs text-muted-foreground border-b border-border">
                            <th className="text-left py-2 px-2 w-16">Time</th>
                            <th className="text-left py-2 px-2 w-12">Cur</th>
                            <th className="text-center py-2 px-2 w-20">Impact</th>
                            <th className="text-left py-2 px-2">Event</th>
                            <th className="text-right py-2 px-2 w-20 hidden sm:table-cell">Forecast</th>
                            <th className="text-right py-2 px-2 w-20 hidden sm:table-cell">Previous</th>
                            <th className="text-right py-2 px-2 w-24 hidden md:table-cell">Actual</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eventsByDate[date]
                            .sort((a, b) => a.time.localeCompare(b.time))
                            .map((event) => (
                            <tr 
                              key={event.id} 
                              className={`border-b border-border/50 hover:bg-secondary/20 transition-colors ${
                                event.is_past ? 'opacity-60' : ''
                              }`}
                              data-testid={`event-${event.id}`}
                            >
                              <td className="py-3 px-2">
                                <span className="font-mono text-sm">{event.time}</span>
                              </td>
                              <td className="py-3 px-2">
                                <span className="text-lg" title={event.country}>
                                  {getCountryFlag(event.country)}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <div className={`w-2 h-2 rounded-full ${getImpactColor(event.impact)}`}></div>
                                  <div className={`w-2 h-2 rounded-full ${event.impact !== 'low' ? getImpactColor(event.impact) : 'bg-transparent'}`}></div>
                                  <div className={`w-2 h-2 rounded-full ${event.impact === 'high' ? getImpactColor(event.impact) : 'bg-transparent'}`}></div>
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <div>
                                  <span className={`font-medium text-sm ${event.impact === 'high' ? 'text-foreground' : 'text-muted-foreground'}`}>
                                    {event.name}
                                  </span>
                                  <span className="ml-2 text-xs text-muted-foreground hidden lg:inline">
                                    ({event.country})
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-2 text-right hidden sm:table-cell">
                                <span className="font-mono text-sm text-muted-foreground">
                                  {event.forecast || '-'}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-right hidden sm:table-cell">
                                <span className="font-mono text-sm text-muted-foreground">
                                  {event.previous || '-'}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-right hidden md:table-cell">
                                <div className="flex items-center justify-end gap-2">
                                  {event.actual ? (
                                    <>
                                      <span className={`font-mono text-sm font-bold ${
                                        event.surprise > 0 ? 'text-primary' : 
                                        event.surprise < 0 ? 'text-destructive' : 
                                        'text-foreground'
                                      }`}>
                                        {event.actual}
                                      </span>
                                      {getSurpriseIndicator(event)}
                                    </>
                                  ) : (
                                    <span className="font-mono text-sm text-muted-foreground">-</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </motion.div>
        )}

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  </div>
                  <span className="text-muted-foreground">High Impact</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <div className="w-2 h-2 rounded-full bg-transparent"></div>
                  </div>
                  <span className="text-muted-foreground">Medium Impact</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 rounded-full bg-transparent"></div>
                    <div className="w-2 h-2 rounded-full bg-transparent"></div>
                  </div>
                  <span className="text-muted-foreground">Low Impact</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span className="text-muted-foreground">Better than expected</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-destructive" />
                  <span className="text-muted-foreground">Worse than expected</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default EconomicCalendar;
